import React, { useState, useEffect, useRef } from "react";
import { BookOption, Product, ProductSize } from "../types/Product";
import { ProductOption } from "../types/ProductOption";
import { Row, Column, Spacing } from "../helpers/layout";
import { css } from "styled-components";
import { PrintConfigurator } from "./PrintConfigurator";
import { ProductPreview } from "./ProductPreview";
import { BigTitle, ProductSerie } from "../typography";
import {
  calculateOrderItem,
  itemHasWorldwideShipping,
} from "../helpers/product";
import { useUserState } from "../context/UserContext";
import { addToComparison } from "../actions/addToComparison";
import { addToCart } from "../actions/addToCart";
import { addToWishlist } from "../actions/addToWishlist";
import ShippingIcon from "../icons/ShippingIcon";
import { Hr } from "./Hr";
import { Colors } from "../branding";
import { TryInYouRoomButton } from "./TryInYouRoomButton";
import { useResponsive } from "../context/ResponsiveContext";
import { PrintDimensions } from "./PrintDimensions";
import { useCommonData } from "../context/CommonDataContext";
import { ProductNote } from "./ProductNote";
import { ProductDetailFooter } from "./ProductDetailFooter";
import { useComparison } from "../context/ComparisonContext";
import { useWishlist } from "../context/WishlistContext";
import { useCart } from "../context/CartContext";
import { EditorContent } from "./EditorContent";
import { BlocksRenderer } from "./BlocksRenderer";
import { Wrapper } from "./Wrapper";
import uniqBy from "lodash/uniqBy";
import { useRecentlyViewed } from "../context/RecentlyViewedContext";
import { addToRecentlyViewed } from "../actions/addToRecentlyViewed";
import ShopProductAwards from "./ShopProductAwards";
import MobilePrintPreviewModal from "./MobilePrintPreviewModal";
import ProductAdditionalImages from "./ProductAdditionalImages";

export function PrintProductDetail(props: {
  product: Product & {
    book_option?: BookOption | null;
  };
}) {
  const { product } = props;
  const previewRef = useRef<HTMLDivElement>(null);
  const { isAuthenticated } = useUserState();
  const [size, setSize] = useState<ProductSize>(product.sizes[0]);
  const [options, setOptions] = useState<ProductOption[]>([]);
  const { isTablet, isMobile, isDesktop } = useResponsive();
  const { comparison, fetchComparison } = useComparison();
  const { fetchWishlist } = useWishlist();
  const { fetchCart } = useCart();
  const { recentlyViewed, fetchRecentlyViewed } = useRecentlyViewed();
  const { settings } = useCommonData();
  const isSmall = isTablet || isMobile;

  const Layout = isSmall ? Column : Row;

  useEffect(() => {
    setSize(product.sizes[0]);
  }, [product]);

  const calculated = calculateOrderItem({ product, size, options }, settings);

  const productInHomes = product.in_homes.map((c) => c.in_home);
  const relatedInHomes = product.related_in_homes;
  const allInHomes = [...productInHomes, ...relatedInHomes];
  const inHomes = uniqBy(allInHomes, (inHome) => {
    return inHome.id;
  });
  const worldwideShipping = itemHasWorldwideShipping({
    options,
  });

  const imageAspectRatio = product.featured_image
    ? product.featured_image.width / product.featured_image.height
    : 1;

  return (
    <React.Fragment>
      <div
        css={css`
          padding-bottom: ${Spacing.xxl};
        `}
      >
        <Wrapper type="header">
          <Layout gutter={imageAspectRatio > 1 ? Spacing.l : Spacing.xxl}>
            <div
              ref={previewRef}
              css={css`
                flex: 0 0 ${imageAspectRatio > 1 ? "50" : "30"}%;
                /* increased z-index for the magnifier to be over the elements on the right */
                z-index: 10;

                ${isDesktop &&
                css`
                  position: sticky;
                  top: ${Spacing.l};
                `}
              `}
            >
              <Column>
                <ProductPreview
                  product={product}
                  printSize={size}
                  options={options}
                  enableZoom={isDesktop}
                />
                {isSmall && !settings.gallery_focus && (
                  <div
                    css={css`
                      text-align: right;
                    `}
                  >
                    <TryInYouRoomButton
                      productId={product.id}
                      size={size}
                      options={options}
                    />
                  </div>
                )}
                <ProductAdditionalImages inHomes={inHomes} />
                {!isSmall && !settings.gallery_focus && (
                  <>
                    <Hr size="medium" color={Colors.black} />
                    <div>
                      <TryInYouRoomButton
                        productId={product.id}
                        size={size}
                        options={options}
                      />
                    </div>
                  </>
                )}
              </Column>
            </div>
            <div
              css={css`
                flex: 1 0 20%;
              `}
            >
              <Column gutter={Spacing.xxl}>
                <div
                  css={css`
                    text-align: center;
                  `}
                >
                  <BigTitle>{product.name}</BigTitle>
                  {product.serie && (
                    <ProductSerie>{product.serie.name}</ProductSerie>
                  )}
                  {product.product_caption && (
                    <div
                      css={css`
                        margin-top: ${Spacing.xl};
                      `}
                    >
                      <EditorContent text={product.product_caption} />
                    </div>
                  )}
                  {product.note && (
                    <div
                      css={css`
                        margin-top: ${Spacing.xl};
                      `}
                    >
                      <ProductNote note={product.note} />
                    </div>
                  )}
                  <ShopProductAwards awards={product.awards} />
                </div>
                <PrintConfigurator
                  product={product}
                  selected={{
                    size,
                    options,
                  }}
                  onSelectOptions={(options) => setOptions(options)}
                  onSelectSize={(size) => setSize(size)}
                  disablePrices={!isAuthenticated}
                />
                <PrintDimensions
                  calculated={calculated}
                  selectedOptions={options}
                />
                <ProductDetailFooter
                  product={product}
                  total={calculated.total}
                  onAddToCart={async () => {
                    await addToCart({
                      product: product.id,
                      productType: product.type,
                      size,
                      options,
                      book_option: null,
                    });

                    await fetchCart();
                  }}
                  onAddToWishlist={async () => {
                    await addToWishlist({
                      product: product.id,
                      size,
                      options,
                      book_option: null,
                    });

                    await fetchWishlist();

                    // Add item to recently viewed
                    await addToRecentlyViewed(recentlyViewed.uuid, {
                      product: product.id,
                    });

                    await fetchRecentlyViewed();
                  }}
                  onAddToComparison={async () => {
                    await addToComparison(comparison.uuid, {
                      product: product.id,
                      size,
                      options,
                      book_option: null,
                    });

                    await fetchComparison();

                    // Add item to recently viewed
                    await addToRecentlyViewed(recentlyViewed.uuid, {
                      product: product.id,
                    });

                    await fetchRecentlyViewed();
                  }}
                />
                {!worldwideShipping && (
                  <div>
                    <Row align="center">
                      <ShippingIcon />
                      <div
                        css={css`
                          max-width: 600px;
                          line-height: 1.5em;
                        `}
                      >
                        Currently, we are unable to offer shipping for framed
                        prints for delivery to addresses outside of the
                        contiguous US. For international orders, please select
                        unframed.
                      </div>
                    </Row>
                  </div>
                )}
              </Column>
            </div>
          </Layout>
        </Wrapper>
      </div>
      {product.serie && (
        <BlocksRenderer blocks={product.serie.product_blocks} />
      )}
      {settings.product_information && (
        <BlocksRenderer blocks={settings.product_information} />
      )}
      {isMobile && (
        <>
          {options.length > 0 ? (
            <MobilePrintPreviewModal {...{ product, size, options }} />
          ) : null}
          <style>
            {`
            .scroll-top,
            .olark-launch-button {
              display: none !important;
            }
          `}
          </style>
        </>
      )}
    </React.Fragment>
  );
}
